/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { useTranslate } from "@adapptio/lib-components";
import { FieldFontSize, FieldGroup } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { ISchemaStringWithConstConstItem, isModelValueRequired } from "@adapptio/lib-blueprint";
import { InvalidModelNode } from "../../../ui/components/Inspector/InvalidModelNode";
import { IInspectorControl_FontSize, TInspectorControlProps } from "../InspectorControlTypes";

export interface IInspectorControlFontSizeProps extends TInspectorControlProps<IInspectorControl_FontSize> {}

export const InspectorControlFontSize: React.FC<IInspectorControlFontSizeProps> = ({ model: rootModel }) => {
	const t = useTranslate();
	const { model: valueModel, validationErrors } = useBlueprintModel(
		rootModel.value.constant,
		"constString"
	);

	const required = isModelValueRequired(rootModel) || isModelValueRequired(valueModel);

	const inspectorContext = useEditorInspectorContext();

	const constants = useMemo<ISchemaStringWithConstConstItem[]>(() => {
		return rootModel.schema.opts.constants.map((item) => {
			return {
				label: t("editor", item.label),
				value: item.value
			};
		});
	}, [ rootModel ]);

	const handleChange = useCallback(
		(value: string) => {
			if (!valueModel) {
				return;
			}

			valueModel.schema.setValue(valueModel, value, true);
		},
		[ valueModel ]
	);

	if (!valueModel) {
		return <InvalidModelNode model={valueModel} />;
	}

	return (
		<FieldGroup grow>
			<FieldFontSize
				value={valueModel.value}
				constants={constants}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow={true}
				readOnly={inspectorContext?.readOnly}
				clearable={!required}
			/>
		</FieldGroup>
	);
};
